/* eslint-disable prefer-destructuring */
import lod_ from "lodash";
import { Chip, Divider, Icon, IconButton, Tooltip } from "@mui/material";
import { t } from "i18next";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import { useState } from "react";
import DatePresets from "components/Custom/DatePresets";
import DictionaryMenu from "pages/settings/filters/DictionaryMenu";
import { getLabelPath, getLabel } from "helpers/dictionary";
import { useSelector } from "react-redux";

/**
 * Compact inline control to pick a default date preset for a date filter.
 */
const DefaultDateValue = ({ value, onEdit = () => {} }) => {
	const [defaultValue, setDefaultValue] = useState(value);

	return (
		<MDBox display="flex" alignItems="center" flexWrap="wrap" gap={0.75}>
			<MDTypography variant="caption" color="text" fontWeight="medium">
				{t("FILTERS.date.PRESETS.defaultValue")}
			</MDTypography>
			{defaultValue ? (
				<Chip
					size="small"
					color="info"
					variant="outlined"
					label={t(`FILTERS.date.PRESETS.${defaultValue}`)}
					onDelete={() => {
						setDefaultValue("");
						onEdit({ defaultValue: null });
					}}
					sx={{ backgroundColor: "transparent" }}
				/>
			) : (
				<MDTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
					{t("SETTINGS.CHARTS.NEW.noSelection")}
				</MDTypography>
			)}
			<DatePresets
				fullHeight
				handleSelectPreset={({ interval, code }) => {
					setDefaultValue(code);
					onEdit({ defaultValue: code });
				}}
			/>
		</MDBox>
	);
};

/**
 * Header row shared by both filter kinds: type icon + breadcrumb + name + actions.
 */
const FilterRowHeader = ({ icon, breadcrumb, name, children }) => (
	<MDBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
		<MDBox display="flex" alignItems="center" gap={1.25} minWidth={0}>
			<MDBox
				display="flex"
				alignItems="center"
				justifyContent="center"
				sx={({ palette, functions: { rgba } }) => ({
					flexShrink: 0,
					width: 34,
					height: 34,
					borderRadius: "9px",
					color: palette.info.main,
					backgroundColor: rgba(palette.info.main, 0.12)
				})}
			>
				<Icon fontSize="small">{icon}</Icon>
			</MDBox>
			<MDBox minWidth={0}>
				{breadcrumb && (
					<MDTypography
						variant="caption"
						color="text"
						fontWeight="regular"
						sx={{
							display: "block",
							opacity: 0.7,
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis"
						}}
					>
						{breadcrumb}
					</MDTypography>
				)}
				<MDTypography variant="button" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
					{name}
				</MDTypography>
			</MDBox>
		</MDBox>
		<MDBox display="flex" alignItems="center" sx={{ flexShrink: 0 }}>
			{children}
		</MDBox>
	</MDBox>
);

const SpecialFilterItem = ({ item, index, onEdit = () => {}, deleteItem = () => {} }) => {
	const isDate = item.type?.includes?.("date");

	return (
		<MDBox display="flex" flexDirection="column" gap={1}>
			<FilterRowHeader
				icon={isDate ? "event" : "bolt"}
				breadcrumb={t("FILTERS.basicFieldTitle")}
				name={t(`FILTERS.${item.path}`)}
			>
				<Tooltip title={t("SETTINGS.FILTERS.delete")} placement="top" arrow>
					<IconButton size="small" color="error" onClick={() => deleteItem(index)}>
						<Icon fontSize="small">delete</Icon>
					</IconButton>
				</Tooltip>
			</FilterRowHeader>
			{isDate && (
				<>
					<Divider sx={{ my: 0 }} />
					<DefaultDateValue value={item.defaultValue} onEdit={onEdit} />
				</>
			)}
		</MDBox>
	);
};

const DictionaryFilterItem = ({
	item,
	index,
	dictionary: dictionaries,
	deleteItem = () => {},
	language,
	//
	value = "",
	linkItem,
	removeLink,
	onEdit
}) => {
	const codeDictionary = item.dictionary;
	const path = item.path;
	const fullItemPath = `${codeDictionary}.${path}`;

	const dictionary = dictionaries[codeDictionary];

	const dictionaryItem = lod_.get(dictionary?.items ?? {}, path.replaceAll(".", ".items."), {});

	const [dictionaryAnchorEl, setDictionaryAnchorEl] = useState(null);

	const isDate = dictionaryItem.type?.includes?.("date");
	const hasLinks = item.links?.length > 0;

	return (
		<MDBox display="flex" flexDirection="column" gap={1}>
			<FilterRowHeader
				icon={isDate ? "event" : "filter_alt"}
				breadcrumb={getLabelPath(dictionaries, fullItemPath)}
				name={getLabel(dictionaryItem.label, language)}
			>
				<Tooltip title={t("FILTERS.linkField")} placement="top" arrow>
					<IconButton size="small" color="info" onClick={e => setDictionaryAnchorEl(e.currentTarget)}>
						<Icon fontSize="small">link</Icon>
					</IconButton>
				</Tooltip>
				<DictionaryMenu
					dictionary={dictionaries}
					anchorEl={dictionaryAnchorEl}
					handleInsertText={e => {
						if (e !== fullItemPath) {
							linkItem(item, index, e);
						}
						setDictionaryAnchorEl(null);
					}}
					handleClose={() => setDictionaryAnchorEl(null)}
				/>
				<Tooltip title={t("SETTINGS.FILTERS.delete")} placement="top" arrow>
					<IconButton size="small" color="error" onClick={() => deleteItem(index)}>
						<Icon fontSize="small">delete</Icon>
					</IconButton>
				</Tooltip>
			</FilterRowHeader>

			{isDate && (
				<>
					<Divider sx={{ my: 0 }} />
					<DefaultDateValue value={value} onEdit={onEdit} />
				</>
			)}

			{hasLinks && (
				<>
					<Divider sx={{ my: 0 }} />
					<MDBox display="flex" alignItems="center" flexWrap="wrap" gap={0.75}>
						<MDTypography variant="caption" color="text" fontWeight="medium">
							{t("FILTERS.linkedFields")}
						</MDTypography>
						{item.links.map((link, linkIndex) => (
							<Chip
								key={linkIndex}
								size="small"
								variant="outlined"
								icon={<Icon>link</Icon>}
								label={getLabelPath(dictionaries, `${link.dictionary}.${link.path}`)}
								onDelete={() => removeLink(index, linkIndex)}
								sx={{ backgroundColor: "transparent" }}
							/>
						))}
					</MDBox>
				</>
			)}
		</MDBox>
	);
};

/**
 * Renders one filter row content inside the vertical DnD list.
 * @param {*} param0
 * @returns
 */
const FilterItemDndList = ({
	item,
	index,
	dictionary,
	deleteItem = () => {},
	linkItem,
	removeLink,
	onEdit
}) => {
	const user = useSelector(state => state.user);
	const language = user.language;

	const source = item.source;

	if (source === "special") {
		return (
			<SpecialFilterItem
				item={item}
				index={index}
				deleteItem={deleteItem}
				onEdit={edit => onEdit(index, edit)}
			/>
		);
	} else {
		return (
			<DictionaryFilterItem
				item={item}
				index={index}
				dictionary={dictionary}
				deleteItem={deleteItem}
				language={language}
				linkItem={linkItem}
				removeLink={removeLink}
				onEdit={edit => onEdit(index, edit)}
			/>
		);
	}
};

export default FilterItemDndList;
